/* eslint-disable clubmed/no-fake-component */
import type { ThemeKey } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { useState, useEffect, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { FieldProps, FormikProps } from 'formik';
import 'formik';
import moment from 'moment';
import DayPicker from 'react-day-picker';
import type { ClassNames, DayModifiers, LocaleUtils, Modifier, Modifiers } from 'react-day-picker';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import * as MomentLocaleUtils from './Datepicker.utils';
import { DatepickerArrow } from './DatepickerArrow';
import { DatepickerCaption } from './DatepickerCaption';
import { themes } from './Datepicker.themes';

type DateRangeValue = { from: Date | null | undefined; to: Date | null | undefined };

export type DatepickerProps = Partial<FieldProps> & {
  className?: string;
  customModifiers?: Partial<Modifiers>;
  disabledDays?: Modifier[];
  datePickerClassNames?: Partial<ClassNames>;
  hasNavigationByYear?: boolean;
  hasRangeMode?: boolean;
  isDateRange?: boolean;
  isFormikInput?: boolean;
  initialMonth?: Date;
  locale?: string;
  minDate?: Date;
  maxDate?: Date;
  navbarElement?: ReactNode;
  onMouseOver?: (day: Date | null | undefined) => unknown;
  onSelect?: (
    arg0:
      | Date
      | {
          from: Date | null | undefined;
          to: Date | null | undefined;
          modifiers: DayModifiers;
        },
  ) => unknown;
  renderDay?: (day: Date, modifiers: DayModifiers) => ReactNode;
  selectedDays?: Modifier;
  shouldHoverSiblingOpenDays?: boolean;
  theme?: ThemeKey<typeof themes>;
  weekdayElement?: ReactNode;
};

type DatepickerThemeStyles = {
  thRoot: string;
  thDayPicker: ClassNames;
  thDayStart: string;
  thDayEnd: string;
  thDayMiddle: string;
  thDay?: string;
  thDayHovered?: string;
  thDaysHovered?: string;
  thDaysSelected?: string;
  thHeader?: string;
};

const DATEPICKER_HORIZON_IN_MONTHS = 24;
const { isDate, isDayAfter, isDayBefore, isSameDay } = DayPicker.DateUtils;

function isSelectingFirstDay(
  from: Date | null | undefined,
  to: Date | null | undefined,
  day: Date,
): boolean {
  const isBeforeFirstDay = Boolean(from && isDayBefore(day, from));
  const isRangeSelected = Boolean(from && to);
  return !from || isBeforeFirstDay || isRangeSelected;
}

function isChangingSelection(
  from: Date | null | undefined,
  to: Date | null | undefined,
  day: Date,
): boolean {
  return Boolean(from && to && day >= from && day <= to);
}

function isSelectingSameDay(
  from: Date | null | undefined,
  to: Date | null | undefined,
  day: Date,
): boolean {
  return Boolean(from && !to && isSameDay(from, day));
}

export const Datepicker = ({
  className,
  field,
  form,
  isDateRange,
  isFormikInput = false,
  maxDate = moment()
    .add({
      months: DATEPICKER_HORIZON_IN_MONTHS,
    })
    .endOf('month')
    .toDate(),
  minDate = moment().startOf('month').toDate(),
  navbarElement,
  hasNavigationByYear,
  initialMonth,
  locale = moment.locale(),
  renderDay,
  theme = 'default',
  customModifiers = {},
  datePickerClassNames = {},
  disabledDays = [],
  selectedDays,
  hasRangeMode,
  onSelect,
  onMouseOver,
  shouldHoverSiblingOpenDays = false,
  weekdayElement,
}: DatepickerProps) => {
  const { isDesktop } = useResponsive();
  const isCompactTheme = theme === 'compact';
  const selectedDaysAsDate = selectedDays instanceof Date ? selectedDays : null;
  const daysSelected: DateRangeValue =
    !selectedDays || selectedDaysAsDate
      ? { from: selectedDaysAsDate, to: selectedDaysAsDate }
      : (selectedDays as DateRangeValue);
  const formikValues =
    isFormikInput && form && field
      ? ((form as FormikProps<Record<string, DateRangeValue>>).values[field.name] as DateRangeValue)
      : undefined;
  const { from, to }: DateRangeValue = formikValues ?? daysSelected;
  const month = initialMonth || from || minDate || new Date();
  const [hovered, setHovered] = useState<Date | null>(isDateRange ? (from ?? null) : null);
  const [activeMonth, setActiveMonth] = useState<Date>(month);
  const setFromDay = useCallback(
    (day: Date) => {
      form?.setFieldValue(field?.name ?? '', {
        from: day,
        to: null,
      });
      setHovered(null);
    },
    [form, field],
  );
  const setToDay = useCallback(
    (day: Date) => {
      form?.setFieldValue(field?.name ?? '', {
        from,
        to: day,
      });
      setHovered(null);
    },
    [from, form, field],
  );
  const resetDays = useCallback(() => {
    form?.setFieldValue(field?.name ?? '', {
      from: null,
      to: null,
    });
    setHovered(null);
  }, [form, field]);
  const isSelectingStartDay = !from || !isDate(from);

  const isOverridingStartDay = (day: Date) => {
    const startAndEndDaysAreSelected = !!from && isDate(from) && !!to && isDate(to);
    const dayIsBeforeStartDay = !!from && isDate(from) && isDayBefore(day, from);
    return startAndEndDaysAreSelected || dayIsBeforeStartDay;
  };

  const isSelectingEndDay = (day: Date) =>
    !!from && isDate(from) && !isSelectingSameDay(from, to, day) && !isOverridingStartDay(day);

  useEffect(() => {
    setActiveMonth(month);
  }, [moment(month, moment.defaultFormat).format('YYYYMMDD')]);
  const handleDayClick = useCallback(
    (day: Date, modifiers: DayModifiers) => {
      if (isFormikInput) {
        if (!isDateRange) {
          setFromDay(day);
          onSelect?.(day);
        } else {
          if (isSelectingStartDay || isOverridingStartDay(day)) {
            setFromDay(day);
          }

          if (isSelectingSameDay(from, to, day)) {
            resetDays();
          }

          if (isSelectingEndDay(day)) {
            setToDay(day);
          }
        }

        return;
      }

      setHovered(null);

      if (onSelect) {
        if (!hasRangeMode) {
          onSelect({
            from: day,
            to: day,
            modifiers,
          });
        } else if (isSelectingFirstDay(from, to, day) || isChangingSelection(from, to, day)) {
          onSelect({
            from: day,
            to: null,
            modifiers,
          });
        } else if (isSelectingSameDay(from, to, day)) {
          onSelect({
            from: null,
            to: null,
            modifiers,
          });
        } else {
          onSelect({
            from,
            to: day,
            modifiers,
          });
        }
      }
    },
    [from, to],
  );
  const handleDayMouseEnter = useCallback(
    (day: Date, modifiers: DayModifiers & { siblingOpenDays?: boolean }) => {
      if (isFormikInput) {
        if (isDateRange && isSelectingEndDay(day)) {
          setHovered(day);
        }

        return;
      }

      if (!modifiers.siblingOpenDays || shouldHoverSiblingOpenDays) {
        setHovered(day);
      }

      if (onMouseOver) {
        onMouseOver(day);
      }
    },
    [],
  );
  const handleDayMouseLeave = useCallback(() => {
    setHovered(null);
  }, []);
  const addMonthsInRange = useCallback((date: Date, number: number) => {
    const targetMonth = moment(date)
      .add({
        months: number,
      })
      .toDate();

    if (minDate && isDayBefore(targetMonth, minDate)) {
      return minDate;
    }

    if (maxDate && isDayAfter(targetMonth, maxDate)) {
      return maxDate;
    }

    return targetMonth;
  }, []);
  const handleNextMonth = useCallback(() => {
    if (!activeMonth) {
      return;
    }

    setActiveMonth(addMonthsInRange(activeMonth, 1));
  }, [activeMonth]);
  const handlePreviousMonth = useCallback(() => {
    if (!activeMonth) {
      return;
    }

    setActiveMonth(addMonthsInRange(activeMonth, -1));
  }, [activeMonth]);

  const handleNextYear = () => {
    if (!activeMonth) {
      return;
    }

    setActiveMonth(addMonthsInRange(activeMonth, 12));
  };

  const handlePreviousYear = useCallback(() => {
    if (!activeMonth) {
      return;
    }

    setActiveMonth(addMonthsInRange(activeMonth, -12));
  }, [activeMonth]);
  const allDisabledDays = [
    ...disabledDays,
    {
      before: minDate,
      after: maxDate,
    },
  ];
  const allSelectedDays =
    theme === 'compact'
      ? [
          from,
          {
            from,
            to: to || hovered,
          },
        ]
      : [
          from,
          {
            from,
            to: to || hovered,
          },
          hovered,
        ];
  const {
    thDay,
    thDayEnd,
    thDayHovered,
    thDayMiddle,
    thDayPicker,
    thDaysHovered,
    thDaysSelected,
    thDayStart,
    thRoot,
    thHeader,
  } = themes[theme] as DatepickerThemeStyles;
  const modifiers: Partial<Modifiers> = {
    ...customModifiers,
    ...(thDay ? { [thDay]: !isDateRange && from } : null),
    [thDayStart]: isFormikInput ? isDateRange && from : from || (!from && hovered),
    [thDayEnd]: to || (from && hovered),
    ...(thDayHovered ? { [thDayHovered]: hovered && !to ? hovered : undefined } : null),
    [thDayMiddle]: hovered,
    ...(thDaysHovered
      ? {
          [thDaysHovered]:
            from && hovered
              ? {
                  from,
                  to: hovered,
                }
              : [],
        }
      : null),
    ...(thDaysSelected ? { [thDaysSelected]: from && to ? allSelectedDays : [] } : null),
    selected: allSelectedDays as Modifier[],
  } as Partial<Modifiers>;
  let numberOfMonths = DATEPICKER_HORIZON_IN_MONTHS;

  if (isDesktop) {
    numberOfMonths = 2;
  }

  numberOfMonths = isCompactTheme ? numberOfMonths : 1;

  const localeUtilsTyped = MomentLocaleUtils as unknown as LocaleUtils;

  const renderFormik = () => (
    <DayPicker
      canChangeMonth
      firstDayOfWeek={moment.localeData().firstDayOfWeek()}
      fixedWeeks
      locale={locale}
      localeUtils={localeUtilsTyped}
      month={activeMonth}
      showOutsideDays
      showWeekDays
      captionElement={
        <DatepickerCaption
          fromMonth={minDate}
          isFormikInput={isFormikInput}
          locale={locale}
          month={activeMonth}
          onNext={handleNextMonth}
          onPrevious={handlePreviousMonth}
          toMonth={maxDate}
        />
      }
      classNames={thDayPicker}
      disabledDays={[
        {
          before: minDate,
          after: maxDate,
        },
        ...disabledDays,
      ]}
      modifiers={modifiers}
      onDayClick={handleDayClick}
      onDayMouseEnter={handleDayMouseEnter}
      onDayTouchEnd={handleDayMouseEnter}
    />
  );

  const render = () => (
    <div className={classnames(thRoot, className)}>
      <DayPicker
        canChangeMonth={isDesktop || !isCompactTheme}
        firstDayOfWeek={moment.localeData().firstDayOfWeek()}
        fixedWeeks={!isCompactTheme}
        locale={locale}
        localeUtils={localeUtilsTyped}
        showOutsideDays={!isCompactTheme}
        showWeekDays
        {...(isDesktop || !isCompactTheme
          ? {
              captionElement: (
                <DatepickerCaption
                  className={thDayPicker.caption}
                  fromMonth={minDate}
                  hasNavigationByYear={hasNavigationByYear}
                  isFormikInput={isFormikInput}
                  onNext={handleNextYear}
                  onPrevious={handlePreviousYear}
                  toMonth={maxDate}
                />
              ),
            }
          : {})}
        classNames={{ ...thDayPicker, ...datePickerClassNames }}
        disabledDays={allDisabledDays}
        fromMonth={minDate}
        initialMonth={initialMonth}
        modifiers={modifiers}
        month={isCompactTheme && !isDesktop ? moment().startOf('month').toDate() : activeMonth}
        navbarElement={
          navbarElement || (
            <DatepickerArrow
              className={thHeader}
              onNext={handleNextMonth}
              onPrevious={handlePreviousMonth}
              fromMonth={minDate}
              locale={locale}
              toMonth={maxDate}
              displayNextMonth={isCompactTheme}
            />
          )
        }
        numberOfMonths={numberOfMonths}
        onDayClick={handleDayClick}
        onDayMouseEnter={handleDayMouseEnter}
        onDayMouseLeave={handleDayMouseLeave}
        renderDay={renderDay}
        selectedDays={allSelectedDays}
        toMonth={maxDate}
        weekdayElement={weekdayElement}
      />
    </div>
  );

  return isFormikInput ? renderFormik() : render();
};
